import TouchAppIcon from '@mui/icons-material/TouchApp';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, CircularProgress, LinearProgress, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    CustomerData,
    DeviceHardware,
    InstallationCommandResult,
    InstallationCommandResultStatusEnum,
    NewDevice,
} from '../../../../../backendsdk';
import { TrackedButton as Button } from '../../../../../components/TrackedComponents';
import useApi from '../../../../../hooks/api';
import palette from '../../../../ColorPalette';
import { WorkerParams } from '../SetupTool';
import ObjectBus from '../assets/feedback/passengers/object.svg';
import ObjectTruck from '../assets/feedback/property/object.svg';
import FixedContainer from '../ui/FixedContainer';
import { imageHeight } from '../utils/analysis';
import { getStoredValue, setStoredValue } from '../utils/storage';
import { convertParam } from './InstallationParams';

const MAX_DISTANCE_METERS = 30;
const MAX_DISTANCE_FEET = MAX_DISTANCE_METERS * 3.28084;

export const INSTRUCTIONS = {
    ic: ['position', 'occlusion', 'backlight', 'lock'],
    ff: ['position'],
};

interface InCabinOverlayProps {
    hardware?: DeviceHardware;
}

export const InCabinOverlay: React.FC<InCabinOverlayProps> = (props: InCabinOverlayProps) => {
    return (
        <Box
            sx={{
                top: '15%',
                bottom: '15%',
                right: '15%',
                left: props.hardware === DeviceHardware.MitacK220 ? '55%' : '15%',
                border: '1px solid white',
                position: 'absolute',
                boxShadow: '0px 0px 5px 1px #fff, inset 0px 0px 5px 1px #fff',
            }}
        />
    );
};

interface FrontFacingOverlayProps {
    horizontalPixel?: number;
}

export const FrontFacingOverlay: React.FC<FrontFacingOverlayProps> = (props) => {
    if (props.horizontalPixel === undefined) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10,
                    backgroundColor: palette.white,
                    opacity: 0.5,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }
    return (
        <Box
            sx={{
                width: '100%',
                height: '0px',
                borderTop: '1px solid white',
                borderBottom: '1px solid white',
                position: 'absolute',
                top: `${(props.horizontalPixel / imageHeight) * 100}%`,
                boxShadow: '0px 0px 5px 1px #fff, inset 0px 0px 5px 1px #fff',
            }}
        />
    );
};

interface DeviceFeedbackProps {
    horizontalPixel?: number;
    groundPixel?: number;
    minDistance?: number;
    newDevice: NewDevice;
    customer: CustomerData;
    suffix: string;
    setWorkerParams: CallableFunction;
    onInstall: CallableFunction;
    onBack: CallableFunction;
    onNext: CallableFunction;
    dashboardLink: React.ReactNode;
}

const DeviceFeedback: React.FC<DeviceFeedbackProps> = (props) => {
    const [stage, setStage] = useState<number>(0);
    const [commandResult, setCommandResult] = useState<InstallationCommandResult>();
    const [isError, setIsError] = useState<boolean>(false);
    const [imageCounter, setImageCounter] = useState<number>(0);
    const [keyCounter, setKeyCounter] = useState<number>(0);
    const [isLoadingInstall, setIsLoadingInstall] = useState<boolean>(false);
    const [isErrorInstall, setIsErrorInstall] = useState<boolean>(false);
    const [objectOverlay, setObjectOverlay] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [enteredDistance, setEnteredDistance] = useState<string>(
        getStoredValue<string>('OE-entered-distance', props.suffix, ''),
    );
    const { t } = useTranslation();
    const { installApi } = useApi();

    const isUS = props.customer.settings.country === 'us';
    const isMetric = !isUS;
    const units = isMetric ? 'meters' : 'inches';
    const image = props.customer.settings.driving_type === 'passengers' ? ObjectBus : ObjectTruck;
    const minDistance = !!props.minDistance
        ? isMetric
            ? props.minDistance + 0.5
            : convertParam(props.minDistance + 0.5, 'METRIC', 'IMPERIAL')
        : undefined;
    const isDistanceValid =
        minDistance !== undefined &&
        !!enteredDistance &&
        !isNaN(parseFloat(enteredDistance)) &&
        parseFloat(enteredDistance) >= minDistance &&
        parseFloat(enteredDistance) <= (isMetric ? MAX_DISTANCE_METERS : MAX_DISTANCE_FEET);
    const stageCount = objectOverlay ? 4 : 2;

    useEffect(() => {
        setStoredValue<string>('OE-entered-distance', props.suffix, enteredDistance);
    }, [enteredDistance]);

    const getCommandStatus = () => {
        installApi
            .installConnectDeviceIdPost({ deviceId: props.newDevice.imei })
            .then((res) => {
                setImageCounter(0);
                setKeyCounter((prev) => prev + 1);
                setCommandResult(res.data);
            })
            .catch(() => {
                setIsError(true);
            });
    };

    const handleInstall = () => {
        setIsLoadingInstall(true);
        setIsErrorInstall(false);
        props
            .onInstall()
            .then(() => {
                props.onNext();
            })
            .catch(() => {
                setIsErrorInstall(true);
                setIsLoadingInstall(false);
            });
    };

    useEffect(getCommandStatus, []);

    useEffect(() => {
        if (
            commandResult?.status !== InstallationCommandResultStatusEnum.Failure &&
            commandResult?.status !== InstallationCommandResultStatusEnum.NoSupport
        ) {
            if (imageCounter >= 3) {
                // wait for progress bar animation to end
                const timerId = setTimeout(() => {
                    getCommandStatus();
                }, 400);
                return () => clearTimeout(timerId);
            } else {
                const timerId = setTimeout(() => {
                    setImageCounter((prev) => prev + 1);
                }, 1000);
                return () => clearTimeout(timerId);
            }
        }
    }, [imageCounter, commandResult?.status]);

    useEffect(() => {
        if (commandResult?.status === InstallationCommandResultStatusEnum.NoSupport) {
            handleInstall();
        }
    }, [commandResult]);

    if (isLoadingInstall || isErrorInstall) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
                        {t(`setup_tool.installation.header`)}
                    </Typography>
                    <FixedContainer>
                        {isLoadingInstall ? (
                            <>
                                <CircularProgress />
                                <Typography textAlign="center" sx={{ fontWeight: 500, mt: 1 }}>
                                    {t(`setup_tool.installation.params`)}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <WarningIcon color="error" sx={{ fontSize: 24, mb: 0.5 }} />
                                <Typography variant="overline" color="error" sx={{ lineHeight: 1, mb: 2 }}>
                                    {t('setup_tool.installation.error')}
                                </Typography>
                                <Button id="btn-retry" variant="contained" onClick={handleInstall}>
                                    {t('setup_tool.installation.retry')}
                                </Button>
                            </>
                        )}
                    </FixedContainer>
                </Box>
            </Box>
        );
    }

    let content;
    const isStreaming = commandResult?.status === InstallationCommandResultStatusEnum.Streaming;
    if (isError || commandResult?.status === InstallationCommandResultStatusEnum.Failure) {
        content = (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography>{t(`setup_tool.device_feedback.error`)}</Typography>
                <Typography>{t(`setup_tool.troubleshoot.contact`)}</Typography>
            </Box>
        );
    } else if (commandResult?.status === InstallationCommandResultStatusEnum.Started) {
        content = (
            <FixedContainer>
                <TouchAppIcon sx={{ color: palette.primary, fontSize: 48, mb: 1 }} />
                <Box sx={{ width: '175px' }}>
                    <Typography id="press-driver-button" textAlign="center" sx={{ fontWeight: 500 }}>
                        {t(`setup_tool.device_feedback.press_driver_button`)}
                    </Typography>
                </Box>
            </FixedContainer>
        );
    } else if (isStreaming) {
        if (stage === 0) {
            content = (
                <Box key={stage} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <LinearProgress
                        key={`${keyCounter}-progress`}
                        variant="determinate"
                        value={(1 - imageCounter / 3) * 100}
                        sx={{ width: '100%', flexShrink: 0 }}
                    />
                    <FixedContainer style={{ position: 'relative' }}>
                        <img
                            id="ic-image"
                            data-testid="ic-image"
                            src={commandResult.ICImageUrl}
                            width="100%"
                            height="100%"
                            style={{ display: 'block' }}
                        />
                        <InCabinOverlay hardware={props.newDevice.hardware} />
                    </FixedContainer>
                    <ul style={{ width: '100%', paddingLeft: '20px' }}>
                        {INSTRUCTIONS.ic.map((item) => (
                            <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                <Typography>{t(`setup_tool.device_feedback.ic_${item}`)}</Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            );
        } else if (stage === 1 || stage === 3) {
            content = (
                <Box key={stage} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <LinearProgress
                        key={`${keyCounter}-progress`}
                        variant="determinate"
                        value={(1 - imageCounter / 3) * 100}
                        sx={{ width: '100%', flexShrink: 0 }}
                    />
                    <FixedContainer style={{ position: 'relative' }}>
                        <img
                            id="ff-image"
                            data-testid="ff-image"
                            src={commandResult.FFImageUrl}
                            width="100%"
                            height="100%"
                            style={{ display: 'block' }}
                        />
                        <FrontFacingOverlay horizontalPixel={stage === 1 ? props.horizontalPixel : props.groundPixel} />
                    </FixedContainer>
                    <ul style={{ width: '100%', paddingLeft: '20px' }}>
                        {stage === 1 ? (
                            INSTRUCTIONS.ff.map((item) => (
                                <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                    <Typography>{t(`setup_tool.device_feedback.ff_${item}`)}</Typography>
                                </li>
                            ))
                        ) : (
                            <li>
                                <Typography>{t('setup_tool.device_feedback.object_instructions_2')}</Typography>
                            </li>
                        )}
                    </ul>
                    {stage === 1 && !!props.minDistance ? (
                        <Button
                            id="btn-no-horizon"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setObjectOverlay(true);
                                setStage(2);
                            }}
                        >
                            {t('setup_tool.device_feedback.no_horizon')}
                        </Button>
                    ) : null}
                </Box>
            );
        } else if (stage === 2) {
            content = (
                <Box key={stage} sx={{ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
                    <img src={image} alt="object" style={{ width: '100%' }} />
                    <Typography sx={{ my: 2 }}>
                        {t('setup_tool.device_feedback.object_instructions_1', { minDistance, units })}
                    </Typography>
                    <TextField
                        fullWidth
                        required
                        label={t('setup_tool.device_feedback.object_distance', {
                            units: t(`setup_tool.installation_params.units.${units}`),
                        })}
                        value={enteredDistance}
                        onChange={(e) => setEnteredDistance(e.target.value)}
                        error={!!errorMessage}
                        helperText={errorMessage}
                    />
                </Box>
            );
        }
    } else {
        content = (
            <FixedContainer>
                <CircularProgress id="pending" data-testid="pending" />
            </FixedContainer>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    width: '100%',
                    height: isStreaming ? undefined : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography id="device-feedback-header" variant="h6" textAlign="center" sx={{ mb: 2 }}>
                    {t(`setup_tool.device_feedback.header`)}
                </Typography>
                {content}
            </Box>
            <Box sx={{ width: '100%', display: 'flex', mt: 'auto' }}>
                <Box sx={{ flex: 1, mr: 1 }}>
                    <Button
                        id="btn-back"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setStage((prev) => {
                                if (prev > 0) {
                                    setErrorMessage('');
                                    if (prev === 2) {
                                        setObjectOverlay(false);
                                    }
                                    return prev - 1;
                                } else {
                                    props.onBack();
                                    return prev;
                                }
                            });
                        }}
                    >
                        {t('setup_tool.back')}
                    </Button>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Button
                        id="btn-next"
                        data-testid="btn-next"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            setStage((prev) => {
                                if (prev < stageCount - 1) {
                                    if (prev === 2) {
                                        if (isDistanceValid) {
                                            props.setWorkerParams((prev: WorkerParams) => ({
                                                ...prev,
                                                distance: isMetric
                                                    ? parseFloat(enteredDistance)
                                                    : convertParam(parseFloat(enteredDistance), 'METRIC', 'IMPERIAL'),
                                            }));
                                            return prev + 1;
                                        } else {
                                            setErrorMessage(
                                                t(`setup_tool.device_feedback.object_distance_error_${units}`, {
                                                    max: isMetric ? MAX_DISTANCE_METERS : Math.floor(MAX_DISTANCE_FEET),
                                                }),
                                            );
                                            return prev;
                                        }
                                    }
                                    return prev + 1;
                                } else {
                                    handleInstall();
                                    return prev;
                                }
                            });
                        }}
                        disabled={commandResult?.status !== InstallationCommandResultStatusEnum.Streaming}
                    >
                        {t('setup_tool.next')}
                    </Button>
                </Box>
            </Box>
            {props.dashboardLink}
        </Box>
    );
};

export default DeviceFeedback;
